import { useMemo, useState } from 'react'
import { Link, useNavigate, useLocation, useOutletContext, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'
import { useAuthStore } from '@/stores/authStore'
import { authService } from '@/services/authService'
import { LoginForm } from '@/types'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { useTranslation } from '@/i18n/useTranslation'
import { TwoFactorLoginForm } from '@/components/auth/TwoFactorLoginForm'
import { SocialLoginStub } from '@/components/auth/SocialLoginStub'
import type { AuthOutletContext } from '@/layouts/AuthLayout'
import { DEMO_SHOP_VIEWERS, resolveDemoShopViewer } from '@/lib/demoShopViewers'
import { resolveStaffPostLoginPath } from '@/lib/staffLoginRedirect'

type ShopOption = {
  shop_id: number
  shop_name: string
  shop_code?: string | null
}

const StaffLogin = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [challengeId, setChallengeId] = useState<string | null>(null)
  const [shopOptions, setShopOptions] = useState<ShopOption[] | null>(null)
  const { applySession } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { t } = useTranslation('common')
  const { shopBranding } = useOutletContext<AuthOutletContext>()
  const { slug, shopQuery } = shopBranding
  /** Platform ShopQuay staff entry has no tenant shop slug — customer auth is shop-only. */
  const allowCustomerAuthLink = Boolean(slug)

  const from = resolveStaffPostLoginPath(location.state?.from?.pathname)

  const demoViewer = useMemo(
    () => resolveDemoShopViewer(searchParams.get('demo')),
    [searchParams],
  )

  const demoPrefill = useMemo(() => {
    if (demoViewer) {
      return {
        email: demoViewer.email,
        password: demoViewer.password,
      }
    }
    const email = searchParams.get('email')?.trim() || ''
    const password = searchParams.get('password') || ''
    if (email || password) {
      return { email, password }
    }
    return null
  }, [demoViewer, searchParams])

  const [showPassword, setShowPassword] = useState(Boolean(demoPrefill?.password))

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<LoginForm>({
    defaultValues: {
      email: demoPrefill?.email || '',
      password: demoPrefill?.password || '',
    },
  })

  const selectedShopId = watch('shop_id')

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true)
    try {
      const payload: LoginForm = {
        ...data,
        shop_id:
          data.shop_id != null && Number.isFinite(Number(data.shop_id))
            ? Number(data.shop_id)
            : undefined,
      }
      const response = await authService.staffLogin(payload)

      if (response.success && response.data?.shop_selection_required && response.data.shops?.length) {
        setShopOptions(response.data.shops)
        toast(t('auth.staffLogin.selectShopPrompt'))
        return
      }

      if (response.success && response.data?.pending_2fa && response.data.challenge_id) {
        setChallengeId(response.data.challenge_id)
        setShopOptions(null)
        return
      }

      if (response.success && response.data?.token && response.data.user) {
        applySession({
          token: response.data.token,
          user: response.data.user,
          expires_in: response.data.expires_in,
          idle_timeout_minutes: response.data.idle_timeout_minutes,
          max_session_expires_at: response.data.max_session_expires_at,
        })
        toast.success(t('auth.loginSuccess'))
        navigate(from, { replace: true })
      } else {
        toast.error(response.message || t('auth.loginFailed'))
      }
    } catch (error: unknown) {
      const code =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { code?: string; message?: string } } }).response?.data?.code
          : undefined
      const msg =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined
      if (code === 'mfa_setup_required') {
        const challengeId =
          error && typeof error === 'object' && 'response' in error
            ? (error as { response?: { data?: { data?: { challenge_id?: string } } } }).response?.data
                ?.data?.challenge_id
            : undefined
        if (challengeId) {
          navigate(`/auth/setup-2fa?challenge=${encodeURIComponent(challengeId)}`)
          return
        }
        toast.error('Two-factor authentication must be set up before you can sign in.')
        return
      }
      toast.error(msg || t('auth.loginFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  if (challengeId) {
    return (
      <div>
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Two-factor authentication</h2>
          <p className="mt-2 text-sm text-gray-600">Enter your authenticator code to continue.</p>
        </div>
        <TwoFactorLoginForm
          challengeId={challengeId}
          onCancel={() => setChallengeId(null)}
          onSuccess={(data) => {
            applySession(data)
            toast.success(t('auth.loginSuccess'))
            navigate(from, { replace: true })
          }}
        />
      </div>
    )
  }

  return (
    <div>
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">{t('auth.staffLogin.title')}</h2>
        <p className="mt-2 text-sm text-gray-600">{t('auth.staffLogin.subtitle')}</p>
        {demoPrefill ? (
          <p className="mt-3 rounded-md bg-teal-50 px-3 py-2 text-xs text-teal-900">
            {t('auth.staffLogin.demoPrefillHint', {
              shop: demoViewer?.shopHint ?? DEMO_SHOP_VIEWERS[0].shopHint,
            })}
          </p>
        ) : (
          <p className="mt-3 text-xs text-gray-500">{t('auth.staffLogin.platformHelpHint')}</p>
        )}
      </div>

      <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
        <div>
          <label htmlFor="email" className="form-label">
            {t('auth.emailAddress')}
          </label>
          <input
            {...register('email', {
              required: t('auth.validation.emailRequired'),
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: t('auth.validation.invalidEmail'),
              },
            })}
            id="email"
            type="email"
            autoComplete="email"
            className={`input ${errors.email ? 'input-error' : ''}`}
            placeholder={t('auth.placeholderEmail')}
          />
          {errors.email && (
            <p className="form-error">{errors.email.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="password" className="form-label">
            {t('auth.password')}
          </label>
          <div className="relative">
            <input
              {...register('password', {
                required: t('auth.validation.passwordRequired'),
                minLength: {
                  value: 8,
                  message: t('auth.validation.passwordMinLength'),
                },
              })}
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              className={`input pr-10 ${errors.password ? 'input-error' : ''}`}
              placeholder={t('auth.placeholderPassword')}
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <EyeSlashIcon className="h-5 w-5 text-gray-400" />
              ) : (
                <EyeIcon className="h-5 w-5 text-gray-400" />
              )}
            </button>
          </div>
          {errors.password && (
            <p className="form-error">{errors.password.message}</p>
          )}
        </div>

        {shopOptions && shopOptions.length > 0 ? (
          <div>
            <label htmlFor="shop_id" className="form-label">
              {t('auth.staffLogin.selectShop')}
            </label>
            <select
              id="shop_id"
              className="input"
              value={selectedShopId ?? ''}
              onChange={(e) => {
                const value = e.target.value ? Number(e.target.value) : undefined
                setValue('shop_id', value, { shouldValidate: true })
              }}
              required
            >
              <option value="">{t('auth.staffLogin.selectShopPlaceholder')}</option>
              {shopOptions.map((shop) => (
                <option key={shop.shop_id} value={shop.shop_id}>
                  {shop.shop_name}
                  {shop.shop_code ? ` (${shop.shop_code})` : ''}
                </option>
              ))}
            </select>
            <p className="mt-1 text-sm text-gray-500">{t('auth.staffLogin.selectShopHint')}</p>
          </div>
        ) : (
          <div>
            <label htmlFor="shop_id" className="form-label">
              {t('auth.shopIdOptional')}
            </label>
            <input
              {...register('shop_id', {
                valueAsNumber: true,
              })}
              id="shop_id"
              type="number"
              className="input"
              placeholder={t('auth.placeholderShopId')}
            />
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="text-sm">
            <Link
              to="/auth/forgot-password"
              className="font-medium text-primary-600 hover:text-primary-500"
            >
              {t('auth.forgotPassword')}
            </Link>
          </div>
        </div>

        <div>
          <button
            type="submit"
            disabled={isLoading || (Boolean(shopOptions?.length) && !selectedShopId)}
            className="btn btn-primary btn-lg w-full"
          >
            {isLoading ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                {t('auth.signingIn')}
              </>
            ) : (
              t('auth.signIn')
            )}
          </button>
        </div>
      </form>

      <SocialLoginStub context="login" />

      {allowCustomerAuthLink ? (
        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">{t('auth.or')}</span>
            </div>
          </div>

          <div className="mt-6 text-center">
            <Link
              to={`/auth/customer/login${shopQuery}`}
              className="font-medium text-primary-600 hover:text-primary-500"
            >
              {t('auth.staffLogin.customerLoginLink')}
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default StaffLogin
